import { createUser, updateUser } from '@/API/repositories/user';
import { COMPANIES_OPTIONS } from '@/common/constants/Accounting';
import { CURRENCY } from '@/common/constants/currency';
import { handleMapToOptions } from '@/common/functions/handleMapToOptions';
import { useRequestsContext } from '@/common/hooks/requestHook';
import { useMessageQueueContext } from '@/common/hooks/useMessageQueue';
import moment from 'moment';
import { useContext, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { AuthContext } from '@/common/contexts/authContext';
import {
	ACCESS,
	CONSULTANT_ACCESSES,
	CONSULTANT_ROLE,
	MANAGER_ROLE,
	ROLES,
} from './constants';

const useUserForm = ({ users, languageOptions }) => {
	const { authUser } = useContext(AuthContext);

	const [searchParams, setSearchParams] = useSearchParams();
	const [managers, setManagers] = useState();
	const [selectedCurrencyOption, setSelectedCurrencyOption] = useState();
	const [selectedLanguageOption, setSelectedLanguageOption] = useState();
	const [selectedCompany, setSelectedComapny] = useState();
	const [isActive, setIsActive] = useState();
	const [showManagerForm, setShowManagerForm] = useState(false);

	const [selectedRolesOption, setSelectedRolesOption] = useState(
		ROLES.find((role) => CONSULTANT_ROLE === role?.value),
	);
	const [selectedAccessOption, setSelectedAccessOption] = useState(
		ACCESS.filter((access) => CONSULTANT_ACCESSES.includes(access.label)),
	);
	const [user, setUser] = useState();
	const [userManagers, setUserManagers] = useState(user?.managers);

	const nameRef = useRef();
	const surnameRef = useRef();
	const emailRef = useRef();
	const loginRef = useRef();
	const passwordRef = useRef();

	const contractTypeRef = useRef();
	const voipIdRef = useRef();
	const contractHoursRef = useRef();
	const callingSystemRef = useRef();
	const vacationDaysRef = useRef();
	const monthHoursRef = useRef();
	const contractStartRef = useRef();
	const contractEndRef = useRef();
	const bankAccountNumberRef = useRef();

	const { hasUnfilledRequest, makeRequest } = useRequestsContext();
	const { addMessage } = useMessageQueueContext();

	const getCurrentManager = () => {
		const current = userManagers?.find(
			(m) =>
				moment(m.active_to)
					.startOf('day')
					.isSameOrAfter(moment().startOf('day')) &&
				moment(m.active_from)
					.startOf('day')
					.isSameOrBefore(moment().startOf('day')),
		)?.manager;

		return current;
	};

	const isUserAdmin = authUser.role === 'ADMIN';

	const handleSubmit = async (e) => {
		e.preventDefault();

		const payload = {};

		payload.name = nameRef.current.value.replace(' ', '');
		payload.surname = surnameRef.current.value.replace(' ', '');
		payload.email = emailRef.current.value.replace(' ', '');
		payload.username = loginRef.current.value.replace(' ', '');
		payload.password = passwordRef.current?.value.replace(' ', '');
		payload.contract_type = contractTypeRef.current?.value.replace(' ', '');
		payload.role = selectedRolesOption.value;
		payload.managers = userManagers;

		payload.language = selectedLanguageOption.value;
		payload.company = selectedCompany.value;
		payload.active = isActive;
		payload.access = selectedAccessOption.map((data) => data.value);

		payload.contract_start_date = contractStartRef.current?.value;
		payload.contract_end_date = contractEndRef.current?.value ?? undefined;
		payload.voip_id = voipIdRef.current?.value;
		payload.salary_currency = selectedCurrencyOption?.value;
		payload.contract_hours = contractHoursRef.current?.value;
		payload.calling_system = callingSystemRef.current?.checked;
		payload.vacation_days = vacationDaysRef.current?.value;
		payload.month_hours = monthHoursRef.current?.value;
		payload.bank_account_number = bankAccountNumberRef.current?.value;

		const response =
			searchParams.get('mode') === 'create'
				? await makeRequest(createUser.bind(null, payload))
				: await makeRequest(updateUser.bind(null, user?._id, payload));

		if (!response?.data) {
			return addMessage("Couldn't update user", 'error');
		}

		addMessage('Success', 'success');
		setSearchParams({});
	};

	const handleGetUsers = async () => {
		const supperiors = handleMapToOptions(
			users.filter((user) => MANAGER_ROLE === user.role),
			'username',
			'_id',
		);

		setManagers(() => supperiors);
		const userId = searchParams.get('userId');
		const userToEdit = users.find((user) => user._id === userId);

		setSelectedRolesOption(
			ROLES.find((role) =>
				userToEdit
					? role?.value === userToEdit.role
					: role?.value === CONSULTANT_ROLE,
			),
		);

		setSelectedAccessOption(
			ACCESS.filter((access) =>
				userToEdit
					? userToEdit.access.includes(access.label)
					: CONSULTANT_ACCESSES.includes(access.label),
			),
		);

		if (userToEdit) {
			setUser(() => userToEdit);
			setUserManagers(() => userToEdit?.managers);
			setSelectedCurrencyOption(() =>
				CURRENCY.find((curr) => curr?.value === userToEdit.salary_currency),
			);
			setSelectedLanguageOption(() =>
				languageOptions.find((lang) => lang?.value === userToEdit.language),
			);
			setSelectedComapny(() =>
				COMPANIES_OPTIONS.find(
					(company) => company?.value === userToEdit.company,
				),
			);
		}
	};

	useEffect(() => {
		setIsActive(() => (user ? user.active : true));
	}, [user]);

	useEffect(() => {
		handleGetUsers();
	}, []);

	const isLoading = hasUnfilledRequest(createUser, updateUser);

	return {
		user,
		isLoading,
		setSearchParams,
		handleSubmit,
		nameRef,
		surnameRef,
		selectedCurrencyOption,
		setSelectedCurrencyOption,
		emailRef,
		loginRef,
		passwordRef,
		setSelectedLanguageOption,
		selectedLanguageOption,
		setSelectedComapny,
		selectedCompany,
		setSelectedRolesOption,
		selectedRolesOption,
		setSelectedAccessOption,
		setIsActive,
		isActive,
		isUserAdmin,
		selectedAccessOption,
		contractStartRef,
		contractEndRef,
		contractTypeRef,
		contractHoursRef,
		vacationDaysRef,
		monthHoursRef,
		bankAccountNumberRef,
		managers,
		getCurrentManager,
		setShowManagerForm,
		voipIdRef,
		callingSystemRef,
		showManagerForm,
		userManagers,
		setUserManagers,
	};
};

export default useUserForm;
